// Mobile-viewport axis of the dev impersonation editor.
// Rendered as a peer of the roles axis inside DevImpersonationSettingsSection.
// State + commit logic live in useDevImpersonationEditor; this file is pure
// presentation. Dev sync note c238ce65.

import { DEV_VIEWPORT_PRESETS } from "@/components/dev/viewport/devViewportPresets";
import type { PendingViewport } from "./useDevImpersonationEditor";

type ViewportAxisProps = {
  pending: PendingViewport;
  setPending: (next: PendingViewport) => void;
  viewportActive: boolean;
  isDesktopBrowser: boolean;
};

export function DevImpersonationViewportAxis({
  pending,
  setPending,
  viewportActive,
  isDesktopBrowser,
}: ViewportAxisProps) {
  return (
    <div className="space-y-1.5 border-t border-amber-200/80 dark:border-amber-800/60 pt-3">
      <div className="flex items-center justify-between">
        <h3 className="text-xs font-semibold uppercase tracking-widest text-amber-900 dark:text-amber-200">
          Mobile viewport axis
        </h3>
        {viewportActive ? (
          <span className="text-[10px] font-semibold uppercase tracking-wider text-emerald-700 dark:text-emerald-300">
            applied
          </span>
        ) : null}
      </div>
      <p className="text-[11px] text-amber-900/85 dark:text-amber-200/85 leading-snug">
        Independent of impersonation — keep impersonation off and still simulate a phone viewport, or vice versa.
      </p>
      {!isDesktopBrowser ? (
        <p className="text-[10px] text-amber-800/70 dark:text-amber-300/70 leading-snug">
          You appear to be on a real mobile / touch device — viewport simulation is disabled. (Resize the window
          past 900px on a desktop browser to enable.)
        </p>
      ) : null}
      <fieldset className="space-y-1" disabled={!isDesktopBrowser}>
        <legend className="sr-only">Mobile viewport preset</legend>
        <ViewportRadio id="off" label="Off (full window)" pending={pending} setPending={setPending} />
        {DEV_VIEWPORT_PRESETS.map((p) => (
          <ViewportRadio key={p.id} id={p.id} label={p.label} pending={pending} setPending={setPending} />
        ))}
      </fieldset>
    </div>
  );
}

function ViewportRadio({
  id,
  label,
  pending,
  setPending,
}: {
  id: PendingViewport;
  label: string;
  pending: PendingViewport;
  setPending: (next: PendingViewport) => void;
}) {
  const checked = pending === id;
  return (
    <label
      className={
        "flex items-center gap-2 rounded-lg border px-2.5 py-1.5 text-xs cursor-pointer transition " +
        (checked
          ? "border-amber-400 bg-amber-100/70 text-amber-950 dark:border-amber-600 dark:bg-amber-900/30 dark:text-amber-100"
          : "border-amber-200/80 hover:border-amber-300 text-amber-900 dark:border-amber-900/50 dark:text-amber-200 dark:hover:border-amber-700")
      }
    >
      <input
        type="radio"
        name="bb-impersonation-viewport"
        checked={checked}
        onChange={() => setPending(id)}
        className="accent-amber-600"
      />
      <span className="font-medium">{label}</span>
    </label>
  );
}
